import { Button, Col, Row, Skeleton, Space, Tooltip } from "antd";
import {
    CheckCircleFilled,
    UserOutlined,
    ClockCircleOutlined,
    ExpandAltOutlined,
    ShrinkOutlined
} from "@ant-design/icons";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { getElasticSearchService, getKubeService } from "../../backend";
import DeploymentStatusTag from "../../components/DeploymentStatusTag";
import Heading from "../../components/Heading";
import { EnvDeployment } from "../../types";
import { mapAppDeployments } from "../../utils/deployments";
import Logs from "../../components/Logs";
import EnvironmentDeploymentResource from "../../components/EnvironmentDeploymentResource";

const EnvironmentAppDeploymentDetails = () => {
    const { projectId, clusterId, appName, deploymentId } = useParams();
    const [loading, setLoading] = useState(true);
    const [deploymentDetails, setDeploymentDetails] = useState<EnvDeployment>();

    const [eventsExpanded, setEventsExpanded] = useState(false);

    useEffect(() => {
        loadData();

        moment.updateLocale(moment.locale(), { invalidDate: "" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        try {
            const [
                { data: { response: deployments } },
                { data: { deployedMicroservices } }
            ] = await Promise.all([
                getElasticSearchService().getServiceDeploymentHistory(projectId!, appName, clusterId),
                getKubeService().getDeployedMicroservices(clusterId!, projectId!)
            ]);

            const appDeployment = mapAppDeployments(deployments, deployedMicroservices).find(d => d.id === deploymentId);

            setDeploymentDetails(appDeployment);
            return appDeployment;
        } catch (error) {
            console.log("ERROR", error);
        } finally {
            setLoading(false);
        }
    }

    const PageTitle = () => (
        <div className="flex-justify-space-between">
            <Space>
                {
                    deploymentDetails?.active &&
                    <Tooltip title="The deployment that is available to your end users.">
                        <CheckCircleFilled style={{
                            fontSize: 24,
                            color: "green"
                        }} />
                    </Tooltip>
                }
                <div>{deploymentDetails?.id}</div>
                <DeploymentStatusTag size="large" status={deploymentDetails?.status.name!} />
            </Space>
        </div>
    )

    const PageDescription = () => (
        <Space direction="vertical">
            APPLICATION
            <Space>
                {/* <ThunderboltOutlined /> */}
                <Space>
                    <span>
                        <UserOutlined /> {deploymentDetails?.createdBy}
                    </span>
                    <span>
                        <ClockCircleOutlined /> {deploymentDetails?.createdAt}
                    </span>
                </Space>
            </Space>
        </Space>
    )

    return (
        loading ?
            <Skeleton /> :
            <>
                <Heading
                    title={<PageTitle />}
                    description={<PageDescription />}
                    level={3}
                />
                <Row gutter={24}>
                    {/* Events */}
                    <Col span={eventsExpanded ? 24 : 16}>
                        <Logs
                            title="Deployments Logs"
                            extra={
                                <Button
                                    type="link"
                                    style={{ paddingRight: 0, color: "white" }}
                                    icon={eventsExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
                                    onClick={() => setEventsExpanded(!eventsExpanded)}
                                >
                                    {eventsExpanded ? "Show resources" : "Expand"}
                                </Button>
                            }>
                            {
                                deploymentDetails?.error ?
                                    <div style={{ color: "var(--error-color)" }}>
                                        [DEPLOYMENT_FAILED] {deploymentDetails.error}
                                    </div>
                                    :
                                    <>
                                        <span>
                                            The application was successfully deployed.
                                        </span>
                                        <br />
                                        <br />

                                        <div>
                                            Application name: <span style={{ color: "#097BB5" }}>{deploymentDetails?.resources[0].name}</span>
                                        </div>
                                        <div>
                                            Version: <span style={{ color: "#097BB5" }}>{deploymentDetails?.resources[0].version}</span>
                                        </div>
                                        <div>
                                            Cluster: <span style={{ color: "#097BB5" }}>{deploymentDetails?.resources[0].kubernetesId}</span>
                                        </div>
                                    </>
                            }
                        </Logs>
                    </Col>

                    {/* App details */}
                    {
                        !eventsExpanded &&
                        <Col span={8}>
                            {
                                deploymentDetails?.resources
                                    .map(r => (<EnvironmentDeploymentResource deploymentType={deploymentDetails.type} resource={r} key={r.id} />))
                            }
                        </Col>
                    }
                </Row>
            </>
    )
}

export default EnvironmentAppDeploymentDetails;